<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'ISO 9001 in Practice — QikSolve',
  description:
    'Learn how ISO 9001 can support everyday quality decisions, records, review, and continual improvement without turning compliance into paperwork.',
});

const page = {
  hero: {
    eyebrow: 'ISO 9001 in practice',
    title: 'Is your quality system documented but difficult to use in practice?',
    summary:
      'ISO 9001 readiness is not just about having the right procedures. It is about whether people know what to do, process owners can see what is happening, leaders can review meaningful evidence, and improvement actions carry through into everyday work.',
    cta: { label: 'Book a discovery call', to: '/contact' },
  },
  intro: {
    title: 'Start with the quality problems you can see',
    content:
      'Many teams do not need more documents first. They need clearer ownership, usable information, consistent review, and a practical way to turn improvement decisions into changed behaviour. ISO 9001 can provide a useful structure for working through those questions without treating quality as a separate paper exercise.',
    items: [
      {
        title: 'Procedures are not consistently followed',
        description: 'The documented process does not always match how work happens, leaving people to rely on local workarounds or personal knowledge.',
      },
      {
        title: 'Reviews produce actions that do not stick',
        description: 'Leadership and management reviews identify issues, but ownership, follow-up, and evidence of improvement are difficult to maintain.',
      },
      {
        title: 'Readiness feels like a document project',
        description: 'The team is preparing records and procedures but still needs a clearer view of how processes, decisions, and evidence work together.',
      },
    ],
  },
  sections: [
    {
      title: 'Why ISO 9001 often becomes difficult in practice',
      items: [
        {
          title: 'The standard is clear; the operating model is not',
          description: 'Many organisations understand the clause structure but still struggle to decide who owns decisions, which evidence is needed, and how the standard fits existing practice.',
        },
        {
          title: 'Document control alone does not create quality',
          description: 'If procedures exist but the work is still improvised, the quality system is not yet embedded in how the team operates.',
        },
        {
          title: 'Review and improvement are often disconnected',
          description: 'Performance review, risk-based thinking, corrective actions, and continual improvement need to be connected to real operational decisions.',
        },
        {
          title: 'A system can be compliant on paper and unstable in practice',
          description: 'Without clarity on ownership, review, records, and change discipline, the operating model becomes fragile and difficult to sustain.',
        },
      ],
    },
    {
      title: 'What good ISO 9001 practice looks like',
      items: [
        {
          title: 'Leadership is visible in decisions',
          description: 'Quality objectives, accountability, review cadence, and the expected way of working are connected to real operational priorities.',
        },
        {
          title: 'Documented information supports work, not just compliance',
          description: 'Procedures, records, templates, and review evidence are usable by the people who must apply them daily.',
        },
        {
          title: 'Risk and improvement are part of normal operations',
          description: 'The organisation looks for weak signals, opportunities to improve, and recurring drivers of variation before they become quality issues.',
        },
        {
          title: 'The system can be reviewed and sustained',
          description: 'The process of checking, improving, and evidencing quality remains understandable as the business changes.',
        },
      ],
    },
    {
      title: 'How QikSolve supports the service pathway',
      items: [
        {
          title: 'Practical quality-system interpretation',
          description: 'We help organisations turn ISO 9001 requirements into an operating model that fits their people, processes, records, and decision-making.',
        },
        {
          title: 'SharePoint and Microsoft 365 governance',
          description: 'The service context often includes use of Microsoft 365 to organise records, ownership, review workflows, and evidence in a more usable way.',
        },
        {
          title: 'Quality-system readiness and change support',
          description: 'We can help clarify where process design, governance, reviews, and document control need attention before a deeper product or implementation path is chosen.',
        },
        {
          title: 'Improvement beyond the standard itself',
          description: 'The goal is not only to meet the standard, but to build a capability for learning, evidence, and operational discipline over time.',
        },
      ],
    },
  ],
  callout: {
    title: 'A practical path forward',
    content:
      'QikSolve helps teams turn ISO 9001 requirements into workable quality practice, including clearer ownership, records, review, and continual improvement. Where a specific digital workflow or platform is the right next step, we can help identify the appropriate pathway. QikSolve is not a certification body, and certification decisions remain with your accredited certification body.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Related paths for the next step',
    items: [
      {
        label: 'Regulation to practice',
        description: 'See how regulatory expectations become workable operating practice across quality and compliance work.',
        to: '/product/regulatory-compliance',
      },
      {
        label: 'Practical GxP quality systems',
        description: 'Understand how a quality system can be usable, controlled, and sustainable alongside daily work.',
        to: '/product/quality-systems',
      },
      {
        label: 'ISO 9001 readiness planning',
        description: 'Prepare for ISO 9001 readiness as a practical operating-model decision, not a document-only exercise.',
        to: '/product/iso-9001-readiness-planning',
      },
      {
        label: 'Records and management review',
        description: 'Connect documented information, management review, ownership, and improvement in daily quality practice.',
        to: '/product/iso-9001-records-and-management-review',
      },
      {
        label: 'SharePoint governance',
        description: 'Explore how Microsoft 365 can support controlled records, permissions, and governance in practice.',
        to: '/product/sharepoint-governance',
      },
      {
        label: 'Map and implement your quality process',
        description: 'Explore QikSolve delivery support for process mapping, ownership, controlled documentation, and implementation readiness.',
        to: '/services/quality-process-mapping-development-and-implementation',
      },
      {
        label: 'A practical SharePoint example',
        description: 'See how SharePoint can support ISO 9001 quality records and review when configured around intended use.',
        to: '/blog/iso-9001-qms',
      },
      {
        label: 'Explore INQ Quality Essentials',
        description: 'Assess whether INQ provides the right focused digital quality-system pathway for your ISO 9001 scope.',
        to: 'https://inq.qiksolve.com/solutions/iso-9001',
      },
    ],
  },
  cta: {
    title: 'Ready to work through your ISO 9001 context?',
    content:
      'Bring the questions your team is facing around quality management, process ownership, review, controls, and evidence. We can help identify the most sensible next step.',
    label: 'Contact QikSolve',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
